import React from 'react';
import {
    Menu,
    MenuTrigger,
    MenuPopover,
    MenuList,
    MenuItem,
    MenuButton,
    Tooltip,
    useIsOverflowItemVisible,
    useOverflowMenu,
} from '@fluentui/react-components';
import { MoreHorizontalRegular } from '@fluentui/react-icons';
import loc from 'owa-localize';
import { observer } from 'owa-mobx-react';
import { useComputedValue } from 'owa-react-hooks/lib/useComputed';
import toggleHeaderPane from '../../orchestration/toggleHeaderPane';
import type { OwaHeaderButtonControl } from '../../types/OwaHeaderButtonControl';
import { overflowMenuButtonTooltip } from './OwaHeaderButton.locstring.json';
import { headerButton, buttonIcon, buttonBadge } from './OwaHeaderButtonGroup.scss';
import OwaHeaderButtonBadge from './OwaHeaderButtonBadge';

const overflowItemIcon = <MoreHorizontalRegular />;
const overflowMenuButtonId = 'owaOverflowMenuBtn_container';

const OwaHeaderOverflowMenu: React.FC<{
    buttons: OwaHeaderButtonControl[];
}> = observer(({ buttons }) => {
    const { ref, isOverflowing } = useOverflowMenu<HTMLButtonElement>();

    if (!isOverflowing) {
        return null;
    }

    return (
        <Menu>
            <Tooltip content={loc(overflowMenuButtonTooltip)} relationship="description">
                <MenuTrigger disableButtonEnhancement={true}>
                    <MenuButton
                        id={overflowMenuButtonId}
                        ref={ref}
                        icon={overflowItemIcon}
                        className={headerButton}
                    ></MenuButton>
                </MenuTrigger>
            </Tooltip>

            <MenuPopover>
                <MenuList>
                    {buttons.map(button => {
                        return <OverflowMenuItem key={button.id} {...button} />;
                    })}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
}, 'OwaHeaderOverflowMenu');

const OverflowMenuItem: React.FC<OwaHeaderButtonControl> = observer(
    ({ id, renderLabel, renderIcon, onClick, pane, badge }) => {
        const isVisible = useIsOverflowItemVisible(id);

        const handleClick = React.useCallback(() => {
            onClick?.();
            if (pane) {
                toggleHeaderPane(pane.id);
            }
        }, [pane, onClick]);

        const itemIcon = useComputedValue(() => {
            return (
                <div className={buttonIcon}>
                    {renderIcon()}
                    {badge?.getCount && (
                        <OwaHeaderButtonBadge
                            id={id}
                            getCount={badge.getCount}
                            className={buttonBadge}
                        />
                    )}
                </div>
            );
        }, [renderIcon, id, badge?.getCount]);

        if (isVisible) {
            return null;
        }

        return (
            <MenuItem icon={itemIcon} onClick={handleClick}>
                {renderLabel()}
            </MenuItem>
        );
    },
    'OverflowMenuItem'
);

export default OwaHeaderOverflowMenu;
